import React, { useState } from 'react';
import { X, Upload, FileSpreadsheet, AlertCircle, CheckCircle, Download } from 'lucide-react';
import { adminApi } from '../services/api';
import { extractApiError } from '../utils/errorUtils';

interface ImportError {
  row: number;
  field: string;
  message: string;
}

interface ImportResult {
  total: number;
  created: number;
  skipped: number;
  errors: ImportError[];
}

interface FairImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type ImportMode = 'file' | 'sheets';

export default function FairImportModal({ isOpen, onClose, onSuccess }: FairImportModalProps) {
  const [mode, setMode] = useState<ImportMode>('file');
  const [file, setFile] = useState<File | null>(null);
  const [sheetsUrl, setSheetsUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleReset = () => {
    setFile(null);
    setSheetsUrl('');
    setResult(null);
    setError(null);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Validate file type
      const validTypes = ['.csv', '.xlsx', '.xls'];
      const parts = selectedFile.name.split('.');
      const extension = parts.length > 1 ? parts[parts.length - 1] : '';
      const fileExt = '.' + extension.toLowerCase();
      if (!validTypes.includes(fileExt)) {
        setError('Invalid file type. Please upload a CSV or Excel file.');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleImport = async () => {
    setError(null);
    setResult(null);

    // Validation
    if (mode === 'file' && !file) {
      setError('Please select a file to import');
      return;
    }

    if (mode === 'sheets' && !sheetsUrl.trim()) {
      setError('Please enter a Google Sheets URL');
      return;
    }

    if (mode === 'sheets' && !sheetsUrl.startsWith('https://docs.google.com/spreadsheets/')) {
      setError('Invalid Google Sheets URL');
      return;
    }

    setImporting(true);

    try {
      const formData = new FormData();

      if (mode === 'file' && file) {
        formData.append('file', file);
      } else if (mode === 'sheets') {
        // Ensure format=csv parameter is present
        let url = sheetsUrl.trim();
        if (!url.includes('format=csv')) {
          url += url.includes('?') ? '&format=csv' : '?format=csv';
        }
        formData.append('sheets_url', url);
      }

      const importResult = await adminApi.importFairs(formData);
      setResult(importResult);

      // If successful (any fairs created), call onSuccess to refresh the fair list
      if (importResult.created > 0) {
        onSuccess();
      }
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to import fairs:', apiError);
      setError(apiError.message || 'Failed to import fairs. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  const downloadErrorReport = () => {
    if (!result || result.errors.length === 0) return;

    // Generate CSV error report
    const csvHeader = 'Row,Field,Error Message\n';
    const csvRows = result.errors
      .map(err => `${err.row},"${err.field}","${err.message}"`)
      .join('\n');
    const csvContent = csvHeader + csvRows;

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fair-import-errors-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Import Fairs</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Import Mode Selection */}
          {!result && (
            <div className="space-y-4">
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setMode('file');
                    setSheetsUrl('');
                    setError(null);
                  }}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition-colors ${
                    mode === 'file'
                      ? 'border-primary bg-teal-50 text-primary-dark'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Upload className="h-5 w-5" />
                    <span className="font-medium">Upload File</span>
                  </div>
                  <p className="text-xs mt-1 text-gray-500">CSV or Excel (.xlsx)</p>
                </button>

                <button
                  onClick={() => {
                    setMode('sheets');
                    setFile(null);
                    setError(null);
                  }}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition-colors ${
                    mode === 'sheets'
                      ? 'border-primary bg-teal-50 text-primary-dark'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <FileSpreadsheet className="h-5 w-5" />
                    <span className="font-medium">Google Sheets</span>
                  </div>
                  <p className="text-xs mt-1 text-gray-500">Paste export URL</p>
                </button>
              </div>

              {/* File Upload */}
              {mode === 'file' && (
                <div className="space-y-3">
                  <label className="block">
                    <span className="text-sm font-medium text-gray-700">Select File</span>
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileChange}
                      className="mt-1 block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-md file:border-0
                        file:text-sm file:font-semibold
                        file:bg-teal-50 file:text-primary-dark
                        hover:file:bg-teal-100
                        cursor-pointer"
                    />
                  </label>
                  {file && (
                    <div className="text-sm text-gray-600 flex items-center gap-2">
                      <FileSpreadsheet className="h-4 w-4" />
                      <span>{file.name}</span>
                      <span className="text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
                    </div>
                  )}
                </div>
              )}

              {/* Google Sheets URL */}
              {mode === 'sheets' && (
                <div className="space-y-3">
                  <label className="block">
                    <span className="text-sm font-medium text-gray-700">Google Sheets Export URL</span>
                    <input
                      type="text"
                      value={sheetsUrl}
                      onChange={(e) => setSheetsUrl(e.target.value)}
                      placeholder="https://docs.google.com/spreadsheets/d/.../export?format=csv"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm"
                    />
                  </label>
                  <div className="bg-secondary-light/10 border border-primary-light rounded-md p-3">
                    <p className="text-xs text-primary-dark">
                      <strong>How to get the export URL:</strong><br />
                      1. Open your Google Sheet<br />
                      2. Go to File → Share → Publish to web<br />
                      3. Choose "Comma-separated values (.csv)" format<br />
                      4. Copy the generated link and paste it here
                    </p>
                  </div>
                </div>
              )}

              {/* Required Fields Info */}
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Required Columns</h3>
                <div className="text-xs text-gray-600 space-y-1">
                  <p><code className="bg-white px-1 py-0.5 rounded">name</code> - Fair name</p>
                  <p><code className="bg-white px-1 py-0.5 rounded">start_date</code> - Start date (YYYY-MM-DD)</p>
                  <p><code className="bg-white px-1 py-0.5 rounded">end_date</code> - End date (YYYY-MM-DD)</p>
                  <p><code className="bg-white px-1 py-0.5 rounded">street</code> - Street address</p>
                  <p><code className="bg-white px-1 py-0.5 rounded">city</code> - City name</p>
                  <p><code className="bg-white px-1 py-0.5 rounded">state</code> - State (2-letter: RI, MA, etc.)</p>
                  <p><code className="bg-white px-1 py-0.5 rounded">zip</code> - ZIP code</p>
                  <p className="mt-2 text-gray-500 italic">All other fields are optional</p>
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}
            </div>
          )}

          {/* Import Result */}
          {result && (
            <div className="space-y-4">
              {/* Success Summary */}
              <div className={`rounded-md p-4 ${
                result.created > 0 ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
              }`}>
                <div className="flex items-start gap-3">
                  {result.created > 0 ? (
                    <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="h-6 w-6 text-yellow-500 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <h3 className={`text-sm font-semibold ${
                      result.created > 0 ? 'text-green-900' : 'text-yellow-900'
                    }`}>
                      Import Complete
                    </h3>
                    <div className="mt-2 text-sm space-y-1">
                      <p><strong>Total rows:</strong> {result.total}</p>
                      <p className="text-green-700"><strong>✓ Created:</strong> {result.created}</p>
                      <p className="text-gray-600"><strong>⊘ Skipped (duplicates):</strong> {result.skipped}</p>
                      <p className="text-red-600"><strong>✗ Errors:</strong> {result.errors.length}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Error Details */}
              {result.errors.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-900">Import Errors</h4>
                    <button
                      onClick={downloadErrorReport}
                      className="flex items-center gap-1 text-xs text-secondary hover:text-primary-dark"
                    >
                      <Download className="h-4 w-4" />
                      Download Error Report
                    </button>
                  </div>

                  <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Row</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Field</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Error</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {result.errors.slice(0, 50).map((err, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-gray-900">{err.row}</td>
                            <td className="px-3 py-2 text-gray-700">{err.field}</td>
                            <td className="px-3 py-2 text-red-600">{err.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {result.errors.length > 50 && (
                      <div className="p-2 text-center text-xs text-gray-500 bg-gray-50 border-t">
                        Showing first 50 of {result.errors.length} errors. Download full report for all errors.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-gray-200">
          {!result ? (
            <>
              <button
                onClick={handleClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={importing || (mode === 'file' && !file) || (mode === 'sheets' && !sheetsUrl)}
                className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {importing ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    <span>Importing...</span>
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    <span>Import Fairs</span>
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleReset}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Import Another
              </button>
              <button
                onClick={handleClose}
                className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
              >
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
